import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import { isFoldBlockHeader } from '@/model/EventClass/eventFromReader'
import {
  findStructureBlockEndIndex,
  getStructureBlockRange,
  getStructureSelectionContext,
  hasOrphanLoopBreak,
  isChoiceBlockDropTargetAllowed,
  isChoiceBlockHeaderCode,
  isStructureHeaderCode,
  isStructureOptionCode,
  isStructureReadonlyCode,
} from '@/events/list/structureRules'

export type InsertPlacement = {
  insertAt: number
  previousIndex: number
}

export type InsertRejectReason =
  | 'empty'
  | 'readonly-anchor'
  | 'drop-target-forbidden'
  | 'choice-block-position'
  | 'selection-context'
  | 'orphan-loop-break'

export type ValidateEventInsertInput = {
  targetList: EventBase[]
  placement: InsertPlacement
  insertingEvents: EventBase[]
  selectionIndex?: number | null
  allowReadonlyAnchor?: boolean
  dropTarget?: { targetIndex: number; position: 'before' | 'after' }
}

export type ValidateEventInsertResult =
  | { ok: true; nextIndent: number }
  | { ok: false; reason: InsertRejectReason }

function shouldIncreaseIndentAfter(code: number): boolean {
  return isStructureHeaderCode(code) || isStructureOptionCode(code) || isFoldBlockHeader(code)
}

/**
 * 根据插入落点前一行事件计算新块 indent。
 */
function getInsertIndentAfterIndex(events: EventBase[], anchorIndex: number): number {
  if (anchorIndex < 0 || anchorIndex >= events.length) return 0
  const anchor = events[anchorIndex]
  if (!anchor) return 0
  const baseIndent = Math.max(0, anchor.Indent ?? 0)
  return shouldIncreaseIndentAfter(anchor.Code) ? baseIndent + 1 : baseIndent
}

/**
 * 根据 InsertPlacement 计算插入 indent。
 */
function getInsertIndent(events: EventBase[], placement: InsertPlacement): number {
  return getInsertIndentAfterIndex(events, placement.previousIndex)
}

/**
 * 当前选中行之前插入（insertAt = selectedIndex）。
 */
export function resolveInsertPlacementFromSelection(
  events: EventBase[],
  selectedIndex: number | null,
): InsertPlacement {
  if (selectedIndex == null || selectedIndex < 0 || selectedIndex >= events.length) {
    return { insertAt: events.length, previousIndex: events.length - 1 }
  }
  return {
    insertAt: selectedIndex,
    previousIndex: selectedIndex - 1,
  }
}

/**
 * 指定行之后插入。
 */
export function resolveInsertPlacementAfterIndex(
  events: EventBase[],
  afterIndex: number,
): InsertPlacement {
  const boundedAfter = Math.max(-1, Math.min(events.length - 1, afterIndex))
  return {
    insertAt: Math.min(events.length, boundedAfter + 1),
    previousIndex: boundedAfter,
  }
}

/**
 * 空事件占位插入点。
 */
export function resolveInsertPlacementFromAddPlaceholder(
  insertAt: number,
  listLength: number,
): InsertPlacement {
  const boundedInsertAt = Math.max(0, Math.min(listLength, insertAt))
  return {
    insertAt: boundedInsertAt,
    previousIndex: Math.max(-1, Math.min(listLength - 1, boundedInsertAt - 1)),
  }
}

/**
 * 拖拽落点换算为 insertAt。
 */
export function resolveInsertPlacementFromDrop(
  events: EventBase[],
  targetIndex: number,
  position: 'before' | 'after',
): InsertPlacement {
  if (targetIndex < 0 || targetIndex >= events.length) {
    return { insertAt: events.length, previousIndex: events.length - 1 }
  }
  if (position === 'after') {
    return {
      insertAt: targetIndex + 1,
      previousIndex: targetIndex,
    }
  }
  return {
    insertAt: targetIndex,
    previousIndex: targetIndex - 1,
  }
}

function getChoiceBlockInsertGuard(
  events: EventBase[],
  insertAt: number,
): { headerIndex: number; headerIndent: number } | null {
  for (let index = Math.min(insertAt - 1, events.length - 1); index >= 0; index -= 1) {
    const event = events[index]
    if (!event || !isChoiceBlockHeaderCode(event.Code)) continue
    const endIndex = findStructureBlockEndIndex(events, index)
    if (insertAt <= index || insertAt > endIndex) continue
    return {
      headerIndex: index,
      headerIndent: Math.max(0, event.Indent ?? 0),
    }
  }
  return null
}

function canInsertAtChoiceBlockPosition(
  events: EventBase[],
  insertAt: number,
  insertIndent: number,
): boolean {
  const guard = getChoiceBlockInsertGuard(events, insertAt)
  if (!guard) return true
  return insertIndent > guard.headerIndent
}

function canInsertWithCurrentSelection(
  events: EventBase[],
  selectedIndex: number | null,
  insertAt: number,
  insertIndent: number,
): boolean {
  if (selectedIndex == null || selectedIndex < 0 || selectedIndex >= events.length) {
    return canInsertAtChoiceBlockPosition(events, insertAt, insertIndent)
  }
  const context = getStructureSelectionContext(events, selectedIndex)
  if (context.kind !== 'none') {
    const headerCode = events[context.headerIndex]?.Code ?? -1
    if (isChoiceBlockHeaderCode(headerCode)) {
      if (context.kind === 'header') {
        return insertAt === context.headerIndex || insertAt === context.endIndex + 1
      }
      if (context.kind === 'option') {
        return insertIndent > Math.max(0, events[selectedIndex]?.Indent ?? 0)
      }
    }
  }
  return canInsertAtChoiceBlockPosition(events, insertAt, insertIndent)
}

function isReadonlyStructureSelection(events: EventBase[], selectedIndex: number | null): boolean {
  if (selectedIndex == null || selectedIndex < 0 || selectedIndex >= events.length) return false
  return isStructureReadonlyCode(events[selectedIndex]?.Code ?? -1)
}

function isPlacementInsideLoop(
  events: EventBase[],
  placement: InsertPlacement,
  nextIndent: number,
): boolean {
  for (let index = placement.insertAt - 1; index >= 0; index -= 1) {
    const event = events[index]
    if (!event || event.Code !== EventID.Loop) continue
    const block = getStructureBlockRange(events, index)
    if (!block) continue
    if (placement.insertAt <= block.endIndex && nextIndent > block.headerIndent) return true
  }
  return false
}

/**
 * 统一插入校验：先判落点是否可插，再判待插入事件是否允许落在此 indent/上下文。
 */
export function validateEventInsert(input: ValidateEventInsertInput): ValidateEventInsertResult {
  const {
    targetList,
    placement,
    insertingEvents,
    selectionIndex = null,
    allowReadonlyAnchor = false,
    dropTarget,
  } = input
  if (!insertingEvents.length) return { ok: false, reason: 'empty' }
  if (dropTarget && !isChoiceBlockDropTargetAllowed(targetList, dropTarget.targetIndex, dropTarget.position)) {
    return { ok: false, reason: 'drop-target-forbidden' }
  }
  if (!allowReadonlyAnchor && isReadonlyStructureSelection(targetList, selectionIndex)) {
    return { ok: false, reason: 'readonly-anchor' }
  }
  const nextIndent = getInsertIndent(targetList, placement)
  const insertAt = placement.insertAt
  if (!canInsertAtChoiceBlockPosition(targetList, insertAt, nextIndent)) {
    return { ok: false, reason: 'choice-block-position' }
  }
  if (selectionIndex != null && !canInsertWithCurrentSelection(targetList, selectionIndex, insertAt, nextIndent)) {
    return { ok: false, reason: 'selection-context' }
  }
  if (hasOrphanLoopBreak(insertingEvents) && !isPlacementInsideLoop(targetList, placement, nextIndent)) {
    return { ok: false, reason: 'orphan-loop-break' }
  }
  return { ok: true, nextIndent }
}

/**
 * 插入拒绝原因对应的用户提示文案。
 */
export function getInsertRejectMessage(reason: InsertRejectReason): string {
  switch (reason) {
    case 'empty':
      return '没有可插入的事件'
    case 'readonly-anchor':
      return '无法在结构壳行插入'
    case 'drop-target-forbidden':
      return '无法移动事件到此处'
    case 'choice-block-position':
      return '无法插入到选项块内'
    case 'selection-context':
      return '无法插入到当前选中位置'
    case 'orphan-loop-break':
      return '中断循环只能插入循环体内'
    default:
      return '无法插入到此处'
  }
}

function revealInsertParent(
  events: EventBase[],
  selectedIndex: number | null,
  insertIndent: number,
): void {
  if (selectedIndex == null || selectedIndex < 0 || selectedIndex >= events.length) return
  const selected = events[selectedIndex]
  if (!selected) return
  if (insertIndent > Math.max(0, selected.Indent ?? 0) && Number(selected.isFold) > 0) {
    selected.isFold = 0
  }
}

/**
 * 将事件块整体 reindent 到目标 indent。
 */
function reindentEventBlock(events: EventBase[], nextIndent: number): EventBase[] {
  if (!events.length) return events
  const baseIndent = Math.max(0, events[0]?.Indent ?? 0)
  const delta = nextIndent - baseIndent
  for (const event of events) {
    event.Indent = Math.max(0, Math.max(0, event.Indent ?? 0) + delta)
  }
  return events
}

/**
 * 在已通过 validateEventInsert 的目标列表指定位置 splice 插入事件块。
 */
export function insertEventsAtPlacement(
  targetList: EventBase[],
  insertingEvents: EventBase[],
  placement: InsertPlacement,
  nextIndent: number,
  options?: { selectionIndex?: number | null },
): { insertAt: number; events: EventBase[] } | null {
  if (!insertingEvents.length) return null
  const insertAt = placement.insertAt
  revealInsertParent(targetList, options?.selectionIndex ?? null, nextIndent)
  reindentEventBlock(insertingEvents, nextIndent)
  for (const event of insertingEvents) {
    event.FoldIndent = 0
    event.SeniorFold = false
  }
  targetList.splice(insertAt, 0, ...insertingEvents)
  return { insertAt, events: insertingEvents }
}

/**
 * 拖拽移动预检：基于移除选中行后的列表判断目标落点是否合法。
 */
export function canMoveEventsAt(
  targetList: EventBase[],
  movingIndices: number[],
  targetIndex: number,
  position: 'before' | 'after',
): boolean {
  if (!movingIndices.length) return false
  const insertBase = position === 'after' ? targetIndex + 1 : targetIndex
  if (movingIndices.includes(targetIndex) || movingIndices.includes(insertBase)) return false
  const targetEvent = targetList[targetIndex]
  if (!targetEvent) return false
  const movingSet = new Set(movingIndices)
  const remaining = targetList.filter((_, index) => !movingSet.has(index))
  const targetIndexInRemaining = remaining.indexOf(targetEvent)
  if (targetIndexInRemaining < 0) return false
  const placement = resolveInsertPlacementFromDrop(remaining, targetIndexInRemaining, position)
  const moved = movingIndices.map((index) => targetList[index]!).filter(Boolean)
  return validateEventInsert({
    targetList: remaining,
    placement,
    insertingEvents: moved,
    dropTarget: { targetIndex: targetIndexInRemaining, position },
  }).ok
}
